import "./CarInfo.css"

import { Button, Form, Input, Modal, Switch, type UploadFile } from "antd"
import { type RcFile } from "antd/es/upload"
import Dragger from "antd/es/upload/Dragger"
import React, { useState } from "react"

import draggerFunctions from "../../../helpers/draggerFunctions"
import { onRequiredTypeChange } from "../../../helpers/format"
import { type ApplicationState } from "../../../store"
import DraggerInside from "../../DraggerInside"

const { confirm } = Modal

export interface CarInfoData {
  hasCarProblem: boolean
  carProblemDescription?: string
  carProblemImages?: UploadFile[]
  cnhImages: UploadFile[]
  licensePlateImages: UploadFile[]
  terms: boolean
}

type OwnProps = {
  afterFinish?: (formData: CarInfoData) => void
  defaults?: ApplicationState["onefleetentrega"]
}

const CarInfo = (props: OwnProps): React.ReactElement => {
  const [formData] = Form.useForm()
  const [carProblemFileList, setCarProblemFileList] = useState<UploadFile[]>(props.defaults?.data.carProblemImages ?? [])
  const [cnhFileList, setCnhFileList] = useState<UploadFile[]>(props.defaults?.data.cnhImages ?? [])
  const [licensePlateFileList, setLicensePlateFileList] = useState<UploadFile[]>(props.defaults?.data.licensePlateImages ?? [])
  const [hasCarProblem, setHasCarProblem] = useState<boolean>(props.defaults?.data.hasCarProblem ?? false)

  function handleFormFinish () {
    showConfirm()
  }

  const handleCarProblemChange = (checked: boolean) => {
    setHasCarProblem(checked)
  }

  const showConfirm = () => {
    confirm({
      title: "TERMO DE RESPONSABILIDADE E CONDIÇÕES PARA UTILIZAÇÃO DOS VEÍCULOS KINTO:",
      content: (
        <div>
          <span>Pelo presente instrumento, DECLARO ter recebido o veículo especificado acima, e ASSUMO total responsabilidade civil e penal por sua condução, comprometendo-me a:</span>
          <ol>
            <li>Utilizar o veículo  em estrita conformidade com as normas de segurança viária, observando rigorosamente todas as leis de trânsito em vigor;</li>
            <li>Comunicar imediatamente à KINTO qualquer ocorrência envolvendo o veículo ;</li>
            <li>Zelar pela manutenção do estado e conservação do veículo;</li>
            <li>Devolver o veículo no mesmo estado em que foi entregue, ressalvado o desgaste natural decorrente do uso e o desgaste de peças consumíveis,</li>
            <li>Entregar ou devolver o veículo, imediatamente e pelo tempo que for necessário, em caso de suspensão do direito de dirigir, ou cassação da respectiva Carteira Nacional de Habilitação;</li>
          </ol>
        </div>
      ),
      async onOk () {
        const fieldValues = formData.getFieldsValue()

        if (props.afterFinish) {
          props.afterFinish({
            hasCarProblem: fieldValues.hasCarProblem,
            carProblemDescription: fieldValues.carProblemDescription,
            carProblemImages: fieldValues.carProblemImages?.fileList,
            cnhImages: fieldValues.cnhImages?.fileList,
            licensePlateImages: fieldValues.licensePlateImages?.fileList,
            terms: true
          })
        }
      },
      cancelText: "Cancelar",
      centered: true,
      okCancel: false
    })
  }

  return (
    <Form
      onFinish={handleFormFinish}
      form={formData}
      layout="vertical"
      requiredMark={onRequiredTypeChange}
    >
      <Form.Item
        name="cnhImages"
        label="Foto da CNH"
        rules={[
          { validator: async (_, value) => { if (!value?.fileList?.length) throw new Error("Imagem não inserida!") } },
          { validator: async (_, value) => { if (value?.fileList?.some((image: RcFile) => image.size > Number(process.env.REACT_APP_MAX_IMAGE_SIZE))) throw new Error("Existem imagens acima do limite") } },
          { validator: async (_, value) => { if (value?.fileList?.some((image: RcFile) => !image.name.match(/\.(png|jpeg|jpg)/g))) throw new Error("Tipo inválido! São aceitos apenas arquivos png, jpg e jpeg!") } }
        ]}
        required={true}
        initialValue={{ file: props.defaults?.data.cnhImages?.[0], fileList: props.defaults?.data.cnhImages }}
      >
        <Dragger
          accept=".png,.jpg,.jpeg"
          onChange={(info) => { draggerFunctions.handleImageChange(info, 2, setCnhFileList) }}
          fileList={cnhFileList}
          customRequest={draggerFunctions.uploadFile}
          beforeUpload={draggerFunctions.beforeUploadFile}
        ><DraggerInside /></Dragger>
      </Form.Item>
      <Form.Item
        name="licensePlateImages"
        label="Foto da placa do veículo"
        rules={[{ validator: async (_, value) => { if (!value?.fileList?.length) throw new Error("Imagem não inserida!") } }]}
        initialValue={{ file: props.defaults?.data.licensePlateImages?.[0], fileList: props.defaults?.data.licensePlateImages }}
        required={true}
      >
        <Dragger
          accept=".png,.jpg,.jpeg"
          onChange={(info) => { draggerFunctions.handleImageChange(info, 2, setLicensePlateFileList) }}
          fileList={licensePlateFileList}
          customRequest={draggerFunctions.uploadFile}
          beforeUpload={draggerFunctions.beforeUploadFile}
        ><DraggerInside /></Dragger>
      </Form.Item>
      <Form.Item
        name="hasCarProblem"
        colon={false}
        valuePropName="checked"
        label="O veículo possui avarias ou itens faltantes?"
        wrapperCol={{ span: 2 }}
        initialValue={props.defaults?.data.hasCarProblem}
      >
        <Switch
          onChange={handleCarProblemChange}
          checkedChildren="Sim"
          unCheckedChildren="Não"
        />
      </Form.Item>
      <Form.Item
        name = "carProblemDescription"
        label= "Descreva qual a avaria ou item faltante"
        required={true}
        rules={[
          { validator: async (_, value) => { if (!value && hasCarProblem) throw new Error("Por favor faça uma breve descrição!") } }
        ]}
        initialValue={props.defaults?.data.carProblemDescription}
        hidden={!hasCarProblem}
      >
        <Input.TextArea size="large" placeholder="Descrição"/>
      </Form.Item>
      <Form.Item
        name="carProblemImages"
        label="Inclua foto das avarias."
        rules={[{ validator: async (_, value) => { if (!value?.fileList?.length && hasCarProblem) throw new Error("Imagem não inserida!") } }]}
        initialValue={{ file: props.defaults?.data.carProblemImages?.[0], fileList: props.defaults?.data.carProblemImages }}
        hidden={!hasCarProblem}
        required={true}
      >
        <Dragger
          accept=".png,.jpg,.jpeg"
          onChange={(info) => { draggerFunctions.handleImageChange(info, 4, setCarProblemFileList) }}
          fileList={carProblemFileList}
          customRequest={draggerFunctions.uploadFile}
          beforeUpload={draggerFunctions.beforeUploadFile}
        ><DraggerInside /></Dragger>
      </Form.Item>
      <Form.Item>
        <Button htmlType="submit">Próximo</Button>
      </Form.Item>
    </Form>
  )
}

export default CarInfo
